import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../services/api";
import {
  AlertCircle,
  Check,
  ExternalLink,
  Loader2,
  Music,
  Pause,
  Pencil,
  Play,
  Plus,
  Search,
  Trash2,
  Wand2,
} from "lucide-react";

const GENRE_OPTIONS = ["", "phonk", "trap", "cinematic", "funk", "pop", "electronic", "other"];
const MOOD_OPTIONS = ["", "energetic", "emotional", "cinematic", "dark", "uplifting", "calm"];
const USAGE_TYPE_OPTIONS = ["", "viral", "storytelling", "transition", "motivation", "general"];
const ENERGY_OPTIONS = ["", "low", "medium", "high"];

const buildEditForm = (sound) => ({
  artist: sound?.artist || "",
  genre: sound?.genre || "",
  mood: sound?.mood || "",
  usage_type: sound?.usage_type || "",
  energy_level: sound?.energy_level || "",
});

const AdminSounds = () => {
  const [sounds, setSounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [editForm, setEditForm] = useState(buildEditForm());
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(new Audio());

  useEffect(() => {
    loadSounds();
    return () => {
      audioRef.current.pause();
    };
  }, []);

  const loadSounds = async () => {
    try {
      setLoading(true);
      const data = await api.listSoundsAdmin();
      setSounds(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao carregar musicas:", error);
    } finally {
      setLoading(false);
    }
  };

  const pendingCount = useMemo(
    () => sounds.filter((sound) => ["auto_enriched", "basic"].includes(sound.metadata_status)).length,
    [sounds]
  );

  const filteredSounds = useMemo(() => {
    return sounds.filter((sound) => {
      const search = searchTerm.trim().toLowerCase();
      const haystack = [
        sound.title,
        sound.artist,
        sound.genre,
        sound.mood,
        sound.usage_type,
        ...(sound.tags || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch = !search || haystack.includes(search);
      const matchesStatus = statusFilter === "all" || sound.metadata_status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [sounds, searchTerm, statusFilter]);

  const handleDelete = async (id) => {
    if (!id) {
      alert("Erro: ID da musica nao encontrado.");
      return;
    }

    if (!window.confirm("Tem certeza que deseja excluir esta musica?")) return;

    try {
      setDeletingId(id);
      await api.deleteSound(id);
      setSounds((prev) => prev.filter((sound) => (sound._id || sound.id) !== id));

      if (playingId === id) {
        audioRef.current.pause();
        setPlayingId(null);
      }
    } catch (error) {
      console.error("Erro ao excluir musica:", error);
      alert("Erro ao excluir musica");
    } finally {
      setDeletingId(null);
    }
  };

  const togglePlay = (sound) => {
    const soundId = sound?._id || sound?.id;
    if (!soundId) return;

    if (playingId === soundId) {
      audioRef.current.pause();
      setPlayingId(null);
      return;
    }

    audioRef.current.src = sound.audio_url;
    audioRef.current.play();
    setPlayingId(soundId);
    audioRef.current.onended = () => setPlayingId(null);
  };

  const startEditing = (sound) => {
    const soundId = sound?._id || sound?.id;
    setEditingId(soundId);
    setEditForm(buildEditForm(sound));
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm(buildEditForm());
  };

  const handleEditField = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (soundId) => {
    try {
      setSavingId(soundId);
      const payload = {
        artist: editForm.artist.trim() || null,
        genre: editForm.genre || null,
        mood: editForm.mood || null,
        usage_type: editForm.usage_type || null,
        energy_level: editForm.energy_level || null,
        metadata_status: "manual",
      };

      const updatedSound = await api.updateSound(soundId, payload);
      setSounds((prev) =>
        prev.map((sound) => ((sound._id || sound.id) === soundId ? updatedSound : sound))
      );
      cancelEditing();
    } catch (error) {
      console.error("Erro ao salvar musica:", error);
      alert("Erro ao salvar metadata da musica");
    } finally {
      setSavingId(null);
    }
  };

  const renderStatus = (status) => {
    const styles = {
      auto_enriched: "bg-[#FFCC00]/10 border-[#FFCC00]/20 text-[#FFCC00]",
      basic: "bg-red-500/10 border-red-500/20 text-red-300",
      mixed: "bg-blue-500/10 border-blue-500/20 text-blue-300",
      manual: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
    };
    const labels = {
      auto_enriched: "Auto",
      basic: "Basico",
      mixed: "Misto",
      manual: "Manual",
    };

    return (
      <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full border ${styles[status] || "bg-white/5 border-white/10 text-white/50"}`}>
        {labels[status] || "Sem status"}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Music className="text-[#FFCC00]" /> Gerenciar Musicas
            </h1>
            <p className="text-white/40 text-sm">Conta D - Musicas Virais</p>
          </div>

          <div className="flex items-center gap-3">
            <div className="px-4 py-2 rounded-xl border border-[#FFCC00]/15 bg-[#FFCC00]/10 text-[#FFCC00] text-sm">
              {pendingCount} pendente{pendingCount === 1 ? "" : "s"} de revisao
            </div>
            <Link
              to="/admin/sounds/new"
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-[#FFCC00] text-black font-bold rounded-xl hover:bg-[#FFCC00]/90 transition-all"
            >
              <Plus className="w-5 h-5" /> Nova Musica
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr,220px] gap-4 mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20" />
            <input
              type="text"
              placeholder="Buscar musica por titulo, artista, mood ou tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/[0.03] border border-white/[0.08] rounded-2xl py-3.5 pl-12 pr-4 focus:outline-none focus:border-[#FFCC00]/40 transition-all"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white/[0.03] border border-white/[0.08] rounded-2xl px-4 py-3.5 text-white focus:outline-none focus:border-[#FFCC00]/40 transition-all"
          >
            <option value="all" className="bg-[#050505]">Todos os status</option>
            <option value="basic" className="bg-[#050505]">Basico</option>
            <option value="auto_enriched" className="bg-[#050505]">Auto</option>
            <option value="mixed" className="bg-[#050505]">Misto</option>
            <option value="manual" className="bg-[#050505]">Manual</option>
          </select>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="flex flex-col items-center py-20 text-white/20">
              <Loader2 className="w-10 h-10 animate-spin mb-2" />
              <p>Carregando biblioteca...</p>
            </div>
          ) : filteredSounds.length > 0 ? (
            filteredSounds.map((sound) => {
              const soundId = sound._id || sound.id;
              const isEditing = editingId === soundId;
              const isSaving = savingId === soundId;

              return (
                <div
                  key={soundId}
                  className="group p-4 bg-white/[0.02] border border-white/[0.05] rounded-2xl hover:bg-white/[0.04] transition-all space-y-4"
                >
                  <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => togglePlay(sound)}
                        className="w-12 h-12 bg-[#FFCC00]/10 rounded-xl flex items-center justify-center text-[#FFCC00] hover:bg-[#FFCC00]/20 transition-all cursor-pointer"
                      >
                        {playingId === soundId ? (
                          <Pause className="w-6 h-6 fill-current" />
                        ) : (
                          <Play className="w-6 h-6 fill-current" />
                        )}
                      </button>

                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-medium text-white/90">{sound.title}</h3>
                          {renderStatus(sound.metadata_status)}
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {sound.genre && (
                            <span className="text-[10px] uppercase tracking-wider bg-white/5 px-2 py-0.5 rounded text-white/50">
                              {sound.genre}
                            </span>
                          )}
                          {sound.mood && (
                            <span className="text-[10px] uppercase tracking-wider bg-[#0056D2]/10 px-2 py-0.5 rounded text-[#8CB8FF]">
                              {sound.mood}
                            </span>
                          )}
                          {sound.usage_type && (
                            <span className="text-[10px] uppercase tracking-wider bg-white/5 px-2 py-0.5 rounded text-white/50">
                              {sound.usage_type}
                            </span>
                          )}
                          {sound.energy_level && (
                            <span className="text-[10px] uppercase tracking-wider bg-white/5 px-2 py-0.5 rounded text-white/50">
                              energia {sound.energy_level}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-2 text-[11px] text-white/35">
                          <span>{sound.artist || "Artista nao definido"}</span>
                          <span>{sound.format || "MP3"}</span>
                          <span>{sound.storage_account}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEditing(sound)}
                        className="p-2 hover:bg-white/5 rounded-lg text-white/40 hover:text-white transition-all"
                        title="Revisar metadata"
                      >
                        {["auto_enriched", "basic"].includes(sound.metadata_status) ? (
                          <Wand2 className="w-5 h-5" />
                        ) : (
                          <Pencil className="w-5 h-5" />
                        )}
                      </button>
                      <a
                        href={sound.audio_url}
                        target="_blank"
                        rel="noreferrer"
                        className="p-2 hover:bg-white/5 rounded-lg text-white/30 hover:text-white transition-all"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </a>
                      <button
                        onClick={() => handleDelete(soundId)}
                        disabled={deletingId === soundId}
                        className="p-2 hover:bg-red-500/10 rounded-lg text-white/30 hover:text-red-500 transition-all cursor-pointer"
                      >
                        {deletingId === soundId ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Trash2 className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {isEditing && (
                    <div className="border border-white/6 rounded-2xl bg-black/20 p-4 space-y-4">
                      <div className="flex items-center gap-2 text-sm text-white/70">
                        <Wand2 className="w-4 h-4 text-[#FFCC00]" />
                        Revisao rapida de metadata
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                        <label className="space-y-2">
                          <span className="text-white/45 text-[11px] uppercase tracking-[0.18em]">Artista</span>
                          <input
                            value={editForm.artist}
                            onChange={(e) => handleEditField("artist", e.target.value)}
                            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-3 text-white text-sm focus:outline-none focus:border-[#FFCC00]/30"
                            placeholder="Artista"
                          />
                        </label>

                        <label className="space-y-2">
                          <span className="text-white/45 text-[11px] uppercase tracking-[0.18em]">Genero</span>
                          <select
                            value={editForm.genre}
                            onChange={(e) => handleEditField("genre", e.target.value)}
                            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-3 text-white text-sm focus:outline-none focus:border-[#FFCC00]/30"
                          >
                            {GENRE_OPTIONS.map((option) => (
                              <option key={option} value={option} className="bg-[#050505]">
                                {option || "Nao definido"}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="space-y-2">
                          <span className="text-white/45 text-[11px] uppercase tracking-[0.18em]">Mood</span>
                          <select
                            value={editForm.mood}
                            onChange={(e) => handleEditField("mood", e.target.value)}
                            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-3 text-white text-sm focus:outline-none focus:border-[#FFCC00]/30"
                          >
                            {MOOD_OPTIONS.map((option) => (
                              <option key={option} value={option} className="bg-[#050505]">
                                {option || "Nao definido"}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="space-y-2">
                          <span className="text-white/45 text-[11px] uppercase tracking-[0.18em]">Uso</span>
                          <select
                            value={editForm.usage_type}
                            onChange={(e) => handleEditField("usage_type", e.target.value)}
                            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-3 text-white text-sm focus:outline-none focus:border-[#FFCC00]/30"
                          >
                            {USAGE_TYPE_OPTIONS.map((option) => (
                              <option key={option} value={option} className="bg-[#050505]">
                                {option || "Nao definido"}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="space-y-2">
                          <span className="text-white/45 text-[11px] uppercase tracking-[0.18em]">Energia</span>
                          <select
                            value={editForm.energy_level}
                            onChange={(e) => handleEditField("energy_level", e.target.value)}
                            className="w-full bg-white/[0.03] border border-white/[0.08] rounded-xl px-3 py-3 text-white text-sm focus:outline-none focus:border-[#FFCC00]/30"
                          >
                            {ENERGY_OPTIONS.map((option) => (
                              <option key={option} value={option} className="bg-[#050505]">
                                {option || "Nao definido"}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>

                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          onClick={cancelEditing}
                          className="px-4 py-2 rounded-xl border border-white/10 text-white/60 hover:bg-white/5 transition-all"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={() => handleSave(soundId)}
                          disabled={isSaving}
                          className="px-4 py-2 rounded-xl bg-[#FFCC00] text-black font-semibold hover:bg-[#FFCC00]/90 transition-all disabled:opacity-50 flex items-center gap-2"
                        >
                          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                          Salvar revisao
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-20 border-2 border-dashed border-white/5 rounded-3xl">
              <AlertCircle className="w-10 h-10 text-white/10 mx-auto mb-3" />
              <p className="text-white/20">Nenhuma musica encontrada.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSounds;
